import { useState, useEffect, useCallback } from 'react';
import { IconCheck, IconClock } from '@tabler/icons-react';
import { Button, Text, Loader, Alert } from '@mantine/core';
import { useRouter } from 'next/router';
import { api } from '~/trpc-extra/api';
import { useAuthData } from '~/hooks/auth/use-auth-data';
import { hasAccessGrantingStatus } from '~/common/stripe/plan-utils';

function subscriptionHasAccess(sub: { status: string; planId?: string | null } | null | undefined): boolean {
  if (!sub) {
    return false;
  }
  return hasAccessGrantingStatus({ status: sub.status, internalPlanId: sub.planId ?? '' });
}

type CheckoutSuccessHandlerProps = {
  onProcessingChange?: (isProcessing: boolean) => void;
};

export function CheckoutSuccessHandler({ onProcessingChange }: CheckoutSuccessHandlerProps) {
  const router = useRouter();
  const { refreshAuthInfo } = useAuthData();
  const isCheckoutSuccess = router.query.checkout === 'success';

  const [pollCount, setPollCount] = useState(0);
  const maxPolls = 10; // Poll for up to ~30 seconds (10 * 3s)

  // Clean up checkout query param once the subscription grants access.
  const cleanupCheckoutParam = useCallback(() => {
    if (!router.isReady) {
      return;
    }

    const { checkout: _, ...restQuery } = router.query;
    void router.replace(
      {
        pathname: router.pathname,
        query: restQuery,
      },
      undefined,
      { shallow: true },
    );
  }, [router]);

  // Use V1 getCurrentSubscription to check for a subscription that grants access.
  const {
    data: subscriptionData,
    refetch: refetchSubscription,
    isLoading: isLoadingSubscription,
    dataUpdatedAt,
  } = api.stripe.getCurrentSubscription.useQuery(undefined, {
    enabled: isCheckoutSuccess,
    refetchInterval: (query) => {
      const { data } = query.state;
      // Stop polling once subscription is active or we've exceeded max polls
      if (subscriptionHasAccess(data?.subscription) || pollCount >= maxPolls) {
        return false;
      }
      return 3000; // Poll every 3 seconds
    },
  });

  // Reset polling state when leaving success mode (prevents stale timeout state)
  useEffect(() => {
    if (!isCheckoutSuccess) {
      setPollCount(0);
    }
  }, [isCheckoutSuccess]);

  // Track poll count for timeout handling.
  const subscriptionStatus = subscriptionData?.subscription?.status;
  useEffect(() => {
    if (!isCheckoutSuccess) {
      return;
    }
    if (!subscriptionData || subscriptionHasAccess(subscriptionData?.subscription)) {
      return;
    }
    setPollCount((prev) => prev + 1);
  }, [dataUpdatedAt, isCheckoutSuccess, subscriptionData, subscriptionStatus]);

  // Determine if we're in a processing state (prevents double-purchase)
  const isProcessing =
    (isCheckoutSuccess && isLoadingSubscription) ||
    (isCheckoutSuccess && !subscriptionData?.subscription && pollCount < maxPolls);

  // Notify parent of processing state changes
  useEffect(() => {
    onProcessingChange?.(isProcessing);
  }, [isProcessing, onProcessingChange]);

  // Once the subscription grants access, refresh PropelAuth's cached org metadata
  // so PaymentRequiredWrapper sees the updated subscription and closes the modal.
  // Then clean up the checkout query param.
  useEffect(() => {
    if (subscriptionHasAccess(subscriptionData?.subscription)) {
      void refreshAuthInfo().then(() => cleanupCheckoutParam());
    }
  }, [subscriptionData, subscriptionStatus, refreshAuthInfo, cleanupCheckoutParam]);

  if (!isCheckoutSuccess) {
    return null;
  }

  // Subscription is active - show success
  if (subscriptionHasAccess(subscriptionData?.subscription)) {
    return (
      <Alert
        icon={<IconCheck size={16} />}
        color="green"
        title={subscriptionStatus === 'trialing' ? 'Trial Active' : 'Subscription Active'}
        className="mb-4"
      >
        <Text size="sm">
          {subscriptionStatus === 'trialing' ?
            'Your free trial is now active. You have full access while the trial is running.'
          : 'Your subscription is now active. Thank you for subscribing!'}
        </Text>
      </Alert>
    );
  }

  // Still processing - show loading state
  if (!subscriptionData?.subscription || isLoadingSubscription) {
    const isTimingOut = pollCount >= maxPolls;

    return (
      <Alert
        icon={isTimingOut ? <IconClock size={16} /> : <Loader size={16} />}
        color={isTimingOut ? 'yellow' : 'blue'}
        title={isTimingOut ? 'Taking longer than expected' : 'Processing your subscription'}
        className="mb-4"
      >
        <div className="flex flex-col gap-2">
          <Text size="sm">
            {isTimingOut ?
              'Your subscription is still being processed. This can take a few minutes.'
            : 'Please wait while we activate your subscription...'}
          </Text>
          {isTimingOut && (
            <Button
              size="xs"
              variant="light"
              onClick={async () => {
                setPollCount(0);
                await refetchSubscription();
              }}
            >
              Check again
            </Button>
          )}
        </div>
      </Alert>
    );
  }

  return null;
}
